import React, { useContext, useState } from 'react';
import { Scope, ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
import { IntlShape } from 'react-intl';
import { useAppDispatch } from 'ducks/hooks';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { resetSheets } from 'ducks/sheets';
import { e2e, getRange } from 'utils/queryUtils';
import { tryParse } from 'utils/dataUtils';
import { getKioskIntegration } from 'utils/integration';
import FallbackScreen from 'components/FallbackScreen/FallbackScreen';

const range = getRange();

const STORAGEONE_PERSISTED_ERROR = `storageone-error-persist-${range}`;

const BoundaryContext = React.createContext<(intl: IntlShape) => void>(() => {});
export const useBoundaryContext = () => useContext(BoundaryContext);

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [intl, setIntl] = useState<IntlShape | undefined>(undefined);
  const [showPlanner, setShowPlanner] = useState(true);
  const [error, setError] = useState<string>('');

  const onError = (error: unknown, _componentStack: string, _eventId: string) => {
    const kioskIntegration = getKioskIntegration();
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    kioskIntegration.error(errorMessage);
    dispatch(resetSheets());
    setShowPlanner(false);
    setError(errorMessage);
  };

  const onReset = (error: unknown, _componentStack: string, _eventId: string) => {
    const timestamp = Date.now();
    const errorData = localStorage.getItem(STORAGEONE_PERSISTED_ERROR);

    const lastError = tryParse<{ error: unknown; timestamp: number }>(
      errorData ?? '',
      value => !!value.timestamp
    );

    // Clear localstorage if last error was less than a minute ago
    if (lastError && timestamp - lastError.timestamp < 60000) {
      localStorage.clear();
    } else {
      localStorage.setItem(STORAGEONE_PERSISTED_ERROR, JSON.stringify({ error, timestamp }));
    }

    history.replace(AppRoutes.HOME);
    window.location.reload();
  };

  const beforeCapture = (scope: Scope) => {
    scope.setTag('boundary', true);
  };

  return (
    <BoundaryContext.Provider value={setIntl}>
      <SentryErrorBoundary
        onError={onError}
        onReset={onReset}
        beforeCapture={beforeCapture}
        fallback={({ resetError }) => (
          <FallbackScreen
            intl={intl}
            onClick={resetError}
            title={({ formatMessage }) => formatMessage({ id: 'storageone.errorBoundaryTitle' })}
            body={({ formatMessage }) =>
              e2e() ? error : formatMessage({ id: 'storageone.errorBoundaryBody' })
            }
            button={({ formatMessage }) =>
              formatMessage({ id: 'storageone.errorBoundaryButtonTitle' })
            }
          />
        )}
      >
        {showPlanner && children}
      </SentryErrorBoundary>
    </BoundaryContext.Provider>
  );
};

export default ErrorBoundary;
